"use client";

import * as React from "react";
import { HexColorPicker } from "react-colorful";
import { cn } from "../../lib/utils";
import ArthaLinkSmIcon from "../icons/arthalinkSmIcon";
import { SeoDrawer } from "./seo-drawer";

const palette = [
  { id: "custom", label: "Custom", isCustom: true },
  { id: "lavender", label: "Lavender", color: "#EBD8FF" },
  { id: "gray", label: "Gray", color: "#D9D9D9" },
  { id: "sun", label: "Sun", color: "#F7C97E" },
  { id: "pink", label: "Pink", color: "#FD90CA" },
  { id: "purple", label: "Purple", color: "#C68CFF" },
];

export interface ShareSettingsContentProps {
  profileName: string;
  profileHandle: string;
  profileAvatar?: string;
  themeColor: string;
  setThemeColor: (color: string) => void;
  onBack: () => void;
  // SEO drawer props
  metaTitle?: string;
  metaDescription?: string;
  onMetaTitleChange?: (value: string) => void;
  onMetaDescriptionChange?: (value: string) => void;
  onSeoSave?: () => void;
  isSeoSaving?: boolean;
}

export function ShareSettingsContent({
  profileName,
  profileHandle,
  profileAvatar,
  themeColor,
  setThemeColor,
  onBack,
  metaTitle = "",
  metaDescription = "",
  onMetaTitleChange,
  onMetaDescriptionChange,
  onSeoSave,
  isSeoSaving = false,
}: ShareSettingsContentProps) {
  const [isSeoDrawerOpen, setIsSeoDrawerOpen] = React.useState(false);
  const [isColorPickerOpen, setIsColorPickerOpen] = React.useState(false);
  const [customColor, setCustomColor] = React.useState(themeColor);

  // Sync custom color with theme color when it changes externally
  React.useEffect(() => {
    setCustomColor(themeColor);
  }, [themeColor]);

  const handleCustomColorClick = () => {
    setIsColorPickerOpen(!isColorPickerOpen);
  };

  const handleColorChange = (color: string) => {
    setCustomColor(color);
    setThemeColor(color);
  };

  const handleSeoClick = () => {
    setIsSeoDrawerOpen(true);
  };

  const handleSeoDrawerClose = () => {
    setIsSeoDrawerOpen(false);
  };
  return (
    <div className="flex flex-col gap-4 overflow-hidden pb-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to share"
          className="flex h-8 w-8 items-center justify-center rounded-md text-[#344054]"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M11.625 3.75L6.375 9L11.625 14.25"
              stroke="#344054"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <p className="text-base font-semibold text-[#344054]">Share Settings</p>
      </div>

      <div
        className="relative rounded-lg border border-black px-4 pb-6 pt-6"
        style={{ background: `linear-gradient(90deg, ${themeColor} 0%, ${themeColor} 100%)` }}
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-[78px] w-[78px] items-center justify-center rounded-full">
            {profileAvatar ? (
              <img
                src={profileAvatar}
                alt={profileName}
                className="h-[70px] w-[70px] rounded-full object-cover"
              />
            ) : (
              <span className="text-xl font-bold uppercase text-black">{profileName.charAt(0)}</span>
            )}
          </div>
          <div className="flex flex-col items-center gap-1">
            <p className="text-[20px] font-black uppercase leading-none text-black font-phudu">{profileName}</p>
            <div className="flex items-center gap-1 text-sm text-[#58616F]">
              <ArthaLinkSmIcon className="h-3.5 w-3.5" />
              <span>{profileHandle}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {palette.map((swatch) => {
          const isSelected = swatch.color ? swatch.color === themeColor : false;
          return (
            <button
              key={swatch.id}
              type="button"
              aria-label={`Select ${swatch.label}`}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full  transition-transform",
                isSelected && "ring-2 ring-offset-[#FFF9EC]"
              )}
              onClick={() => {
                if (swatch.isCustom) {
                  handleCustomColorClick();
                } else if (swatch.color) {
                  setThemeColor(swatch.color);
                }
              }}
            >
              {swatch.isCustom ? (
                <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white">
                  <div className="absolute inset-0 rounded-full border border-black" />
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9 3.375V14.625M14.625 9H3.375"
                      stroke="#00C2A9"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              ) : (
                <span
                  className="block h-8 w-8 rounded-full border border-black"
                  style={{ background: swatch.color }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* <button 
        type="button" 
        className="text-[12px] text-[#344054] cursor-pointer underline underline-offset-2 text-left"
        onClick={handleSeoClick}
      >
        Customize SEO metadata
      </button> */}

      {/* Color Picker */}
      {isColorPickerOpen && (
        <div className="space-y-3 rounded-lg border border-black bg-white p-3 shadow-[0_3px_0_0_#000000]">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#344054]">Custom Color</span>
            <button
              type="button"
              onClick={() => setIsColorPickerOpen(false)}
              className="flex h-6 w-6 items-center justify-center rounded-full text-[#667085] hover:bg-gray-100 hover:text-[#344054] transition-colors"
              aria-label="Close color picker"
            >
              ✕
            </button>
          </div>
          <div className="space-y-2">
            <label htmlFor="hex-input" className="text-xs font-medium text-[#667085]">
              Hex Color
            </label>
            <input
              id="hex-input"
              type="text"
              value={customColor.toUpperCase()}
              onChange={(e) => {
                const hex = e.target.value;
                // Allow # followed by 0-6 hex characters
                if (/^#[0-9A-Fa-f]{0,6}$/.test(hex)) {
                  handleColorChange(hex);
                }
              }}
              className="w-full rounded border border-black px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="#FFFFFF"
              maxLength={7}
            />
          </div>
          <div className="rounded-lg border border-gray-200 p-2">
            <HexColorPicker 
              color={customColor} 
              onChange={handleColorChange} 
              style={{ 
                width: "100%",
                height: "200px"
              }} 
            />
          </div>
          <div className="flex items-center justify-between text-xs text-[#667085]">
            <span>Preview:</span>
            <div 
              className="h-6 w-12 rounded border border-black"
              style={{ backgroundColor: customColor }}
            />
          </div>
        </div>
      )}

      {/* SEO Drawer */}
      <SeoDrawer
        isOpen={isSeoDrawerOpen}
        onClose={handleSeoDrawerClose}
        metaTitle={metaTitle}
        metaDescription={metaDescription}
        onMetaTitleChange={onMetaTitleChange}
        onMetaDescriptionChange={onMetaDescriptionChange}
        onSave={onSeoSave}
        isSaving={isSeoSaving}
      />
    </div>
  );
}
